// "Sidebar click = start over" for pages kept alive by the taskbar.
// Owner 2026-10-16: รายการสั่งซื้อ (/backoffice/orders) sometimes opened on a
// packing round ("หมายเลขตัดรอบแพ็ค : 57692" + Clear No Tracking) instead of the
// normal search page.
//
// ROOT CAUSE. The packing pages (PackingTables.vue, ExpressTables.vue) open the
// round with router.push({ name: "BOOrders", params: { bo_packing_id } }).
// Route params never reach the URL, so that arrival's fullPath is the bare
// "/backoffice/orders" — the same taskbar key, hence the SAME keep-alive
// instance, as the sidebar's plain link. OrderTables.vue writes the round into
// params.bo_packing_id and nothing ever clears it: a later sidebar click lands
// on that instance with no intent (arrivalIntent → refresh false), activated()
// returns early (R1, "a tab switch keeps its state"), and the round stays.
//
// A taskbar-tab click and a sidebar click produce the identical route, so the
// route cannot tell them apart. The sidebar says so itself instead:
// SidebarItem.vue's linkClick raises a flag (navigateWithSidebarReset); the page
// consumes the flag (mounted / activated, and a watcher for a click on the page
// already on screen, where the push is a duplicate and nothing re-activates)
// and goes back to its defaults. A taskbar click sets nothing, so each tab
// keeps its own state; a push with params.bo_packing_id still opens the round.
//
// Only pages listed in SIDEBAR_RESET_PATHS ever get a flag, so no other page
// can be left holding one it will never read.
//
// Keys go through taskbarPathOf, the keep-alive cache identity (as in
// pageRefresh.js). For a listed path the sidebar navigates itself
// (navigateWithSidebarReset) so a cancelled navigation can take its flag back;
// router.js's afterEach drops flags a completed navigation did not target.
// Pinned by tests/unit/ordersSidebarReset*.spec.js.
import Vue from "vue";
import { taskbarPathOf, taskbarKey } from "./taskbar";

export const SIDEBAR_RESET_PATHS = ["/backoffice/orders"];

export const sidebarResetRequests = Vue.observable({ keys: {} });

function keyOf(pathOrFullPath) {
	return typeof pathOrFullPath === "string" ? taskbarPathOf(pathOrFullPath) : "";
}

// true when a flag was set (the path is one that resets on a sidebar click).
export function requestSidebarReset(pathOrFullPath) {
	const key = keyOf(pathOrFullPath);
	if (!key || !SIDEBAR_RESET_PATHS.includes(key)) return false;
	Vue.set(sidebarResetRequests.keys, key, true);
	return true;
}

export function hasSidebarReset(pathOrFullPath) {
	const key = keyOf(pathOrFullPath);
	return !!key && sidebarResetRequests.keys[key] === true;
}

export function consumeSidebarReset(pathOrFullPath) {
	if (!hasSidebarReset(pathOrFullPath)) return false;
	Vue.delete(sidebarResetRequests.keys, keyOf(pathOrFullPath));
	return true;
}

export function isSidebarResetPath(pathOrFullPath) {
	return SIDEBAR_RESET_PATHS.includes(keyOf(pathOrFullPath));
}

// A flag must never outlive the navigation that raised it (verifier #480):
// if that navigation is cancelled — an unsaved-changes leave guard
// (FlashSaleEdit, LandingPageEdit, MtReceiptDetail …) answering next(false),
// or a redirect — a later taskbar click onto the same orders tab would
// otherwise consume it and wipe that tab's state. vue-router 3 has no global
// abort hook and runs leave guards BEFORE beforeEach, so the sidebar does the
// push itself and drops the flag when its own push is rejected.
//
// NavigationDuplicated (the page is already on screen) keeps the flag: the
// page's watcher consumes it. Resolves either way, like router-link.
export function navigateWithSidebarReset(router, path) {
	requestSidebarReset(path);
	return Promise.resolve(router.push(path)).then(
		() => undefined,
		(err) => {
			if (!(err && err.name === "NavigationDuplicated")) consumeSidebarReset(path);
		}
	);
}

// router.afterEach: a navigation that completed somewhere else (a redirect,
// another push that won) drops every flag not meant for `to`.
export function dropSidebarResetsExcept(to) {
	const keep = taskbarKey(to);
	for (const key of Object.keys(sidebarResetRequests.keys)) {
		if (key !== keep) Vue.delete(sidebarResetRequests.keys, key);
	}
}

// The clicks router-link itself would navigate on (its guardEvent): no
// modifier key (new tab/window), left button only.
export function isPlainNavClick(e) {
	if (!e) return true;
	if (e.metaKey || e.altKey || e.ctrlKey || e.shiftKey) return false;
	if (e.button !== undefined && e.button !== 0) return false;
	return true;
}

// The order list's search state as a fresh page has it (OrderTables.vue
// data().params). Dates are passed in so this stays clock-free; mounted()
// fills them with today-2 .. today.
export function defaultOrderListParams(timeFrom = "", timeTo = "") {
	return {
		time_from: timeFrom,
		time_to: timeTo,
		order_status: 0,
		order_number: "",
		tracking_number: "",
		recipient_name: "",
		recipient_phone: "",
		bo_user_id: 0,
		bo_team_id: 0,
		bo_payment_method_id: "",
		bo_customer_id: "",
		bo_sale_channel_id: 0,
		bo_sale_channel_type_id: 0,
		bo_payment_status: "",
		bo_packing_status: "",
		bo_packing_id: "",
		shipping_provider_id: "",
		bo_platform_id: 0,
		payment_method_type: 0,
		force_filter_date: true,
		packing_not_close: 0,
	};
}
